import { SiteId } from 'wly-ecommerce-common';
import {
  AGS_HOST_URL,
  AIGATEWAY_HOST_URL,
  VET_HOST_URL,
  SPECTRABASE_HOST_URL,
  CUSTOMER_PORTAL_URL,
} from '@global/App/constants/constants';
import { getTargetUrl } from '@global/utils/cookie';
import { LOCALE_CODES } from '@global/types/types';

/**
 * Site-specific entitlement configuration
 * Centralizes all site-specific behavior for entitlement checking and UI
 */

export type EntitlementValidationType = 'exactSku' | 'sitePrefix';
export type MyAccountRedirectType = 'customerPortal' | 'subscriptionDetails';

export interface SiteEntitlementConfig {
  // Entitlement validation
  shouldCheckEntitlement: boolean;
  validationType: EntitlementValidationType;
  sitePrefix?: string; // Required if validationType is 'sitePrefix'

  // UI Configuration
  entitlementButton?: {
    buttonLabel?: string;
    titleTranslationKeyPrefix: string;
    supportButtonLabel?: string;
    getButtonUrl: (language?: LOCALE_CODES) => string;
    showActionButton?: boolean;
  };

  // My Account button behavior
  showMyAccountButton?: boolean; // Whether to show My Account button
  myAccountRedirect?: MyAccountRedirectType;

  // Continue Shopping button
  continueShoppingLabel: 'startSearching' | 'continueShopping';
}

/**
 * Default configuration for sites without special entitlement logic
 */
const DEFAULT_CONFIG: SiteEntitlementConfig = {
  shouldCheckEntitlement: false,
  validationType: 'exactSku',
  showMyAccountButton: false,
  continueShoppingLabel: 'continueShopping',
};

/**
 * Site-specific configurations
 * Add new sites here with their specific behavior
 */
export const SITE_ENTITLEMENT_CONFIGS: Partial<
  Record<SiteId, SiteEntitlementConfig>
> = {
  [SiteId.SPECTRABASE]: {
    shouldCheckEntitlement: true,
    validationType: 'exactSku',
    showMyAccountButton: true,
    myAccountRedirect: 'customerPortal',
    continueShoppingLabel: 'startSearching',
    entitlementButton: {
      buttonLabel: 'confirmation.leaveConfirmationPage.startSearching',
      titleTranslationKeyPrefix: 'confirmation.spectrabaseInfo.title',
      supportButtonLabel: 'confirmation.spectrabaseInfo.supportButton',
      getButtonUrl: () => SPECTRABASE_HOST_URL,
    },
  },

  [SiteId.WILA]: {
    shouldCheckEntitlement: true,
    validationType: 'exactSku',
    showMyAccountButton: false, // WILA doesn't show My Account button
    continueShoppingLabel: 'continueShopping',
    entitlementButton: {
      buttonLabel: 'confirmation.readerInfo.buttonLabel',
      titleTranslationKeyPrefix: 'confirmation.readerInfo.title',
      getButtonUrl: () => getTargetUrl(),
    },
  },

  [SiteId.AIGATEWAY]: {
    shouldCheckEntitlement: true,
    validationType: 'exactSku',
    showMyAccountButton: true,
    myAccountRedirect: 'customerPortal',
    continueShoppingLabel: 'startSearching',
    entitlementButton: {
      buttonLabel: 'confirmation.accountInfo.myAccount',
      titleTranslationKeyPrefix: 'confirmation.aiGatewayInfo.title',
      getButtonUrl: () => AIGATEWAY_HOST_URL,
      showActionButton: false,
    },
  },

  [SiteId.AGS]: {
    shouldCheckEntitlement: true,
    validationType: 'sitePrefix',
    sitePrefix: 'ags_',
    showMyAccountButton: true,
    myAccountRedirect: 'subscriptionDetails',
    continueShoppingLabel: 'startSearching',
    entitlementButton: {
      buttonLabel: 'confirmation.leaveConfirmationPage.startSearching',
      titleTranslationKeyPrefix:
        'confirmation.subscriptionEntitlementInfo.title',
      supportButtonLabel:
        'confirmation.subscriptionEntitlementInfo.supportButton',
      getButtonUrl: () => AGS_HOST_URL,
    },
  },

  [SiteId.VET]: {
    shouldCheckEntitlement: true,
    validationType: 'sitePrefix',
    sitePrefix: 'vet_',
    showMyAccountButton: true,
    myAccountRedirect: 'subscriptionDetails',
    continueShoppingLabel: 'startSearching',
    entitlementButton: {
      buttonLabel: 'confirmation.leaveConfirmationPage.startSearching',
      titleTranslationKeyPrefix:
        'confirmation.subscriptionEntitlementInfo.title',
      supportButtonLabel:
        'confirmation.subscriptionEntitlementInfo.supportButton',
      getButtonUrl: () => VET_HOST_URL,
    },
  },
};

/**
 * Get configuration for a specific site
 * Falls back to DEFAULT_CONFIG if site not found
 */
export const getSiteEntitlementConfig = (
  siteId: SiteId
): SiteEntitlementConfig => {
  return SITE_ENTITLEMENT_CONFIGS[siteId] || DEFAULT_CONFIG;
};

/**
 * Get My Account redirect URL for a site
 */
export const getMyAccountUrl = (
  siteId: SiteId,
  language: LOCALE_CODES
): string => {
  const config = getSiteEntitlementConfig(siteId);

  if (config.myAccountRedirect === 'subscriptionDetails') {
    if (siteId === SiteId.AGS || siteId === SiteId.VET) {
      return '/checkout/subscription-details';
    }
    return `/${language}/subscription-details`;
  }

  return CUSTOMER_PORTAL_URL;
};

/**
 * Check if a site should display the My Account button
 */
export const shouldShowMyAccountButton = (siteId: SiteId): boolean => {
  const config = getSiteEntitlementConfig(siteId);
  return config.showMyAccountButton === true;
};
